import angular from 'angular'

import envRaw from '../utils/env-raw'
import Emitter from '../utils/emitter'
import StrictObj from '../utils/strict-obj'
import SumEmitter from '../utils/sum-emitter'
import attachGetters from '../utils/attach-getters'
import attachSetters from '../utils/attach-setters'
import attachEmitters from '../utils/attach-emitters'
import DependentValue from '../utils/dependent-value'

import { getPostInfo, blankPostInfo } from '../utils/post-info'
import { isText, makeEQ, makeOR, makeAND } from '../utils/bits'
import { flow, noop, wrap, isEqual, property, constant, uniqueId, isPlainObject } from 'lodash-es'

import Template from './gallery-modal.component.ejs'
import PartialHead from './partials/gallery-modal.partial.head.ejs'
import PartialVote from './partials/gallery-modal.partial.vote.ejs'
import PartialUser from './partials/gallery-modal.partial.user.ejs'
import PartialIcon from './partials/gallery-modal.partial.icon.ejs'
import PartialDesc from './partials/gallery-modal.partial.desc.ejs'
import PartialPane from './partials/gallery-modal.partial.pane.ejs'
import PartialShare from './partials/gallery-modal.partial.share.ejs'
import PartialButtons from './partials/gallery-modal.partial.buttons.ejs'
import PartialContent from './partials/gallery-modal.partial.content.ejs'
import PartialShowcase from './partials/gallery-modal.partial.showcase.ejs'
import PartialSwitchers from './partials/gallery-modal.partial.switchers.ejs'
import PartialPlaceholder from './partials/gallery-modal.partial.placeholder.ejs'

import LEFT_ICON from '../../img/modal-buttons/white-left.svg'
import RIGHT_ICON from '../../img/modal-buttons/white-right.svg'

angular
  .module('ggs.gallery-modal')
  .component('ggsGalleryModal', {
    template: Template(StrictObj({
      LE: envRaw.liveEditMode,
      partialHead: PartialHead(StrictObj({
        LE: envRaw.liveEditMode,
        partialUser: PartialUser(StrictObj({ LE: envRaw.liveEditMode })),
        partialIcon: PartialIcon(StrictObj({ LE: envRaw.liveEditMode }))
      })),
      partialPane: PartialPane(StrictObj({
        LE: envRaw.liveEditMode,
        partialDesc: PartialDesc(StrictObj({ LE: envRaw.liveEditMode })),
        partialUser: PartialUser(StrictObj({ LE: envRaw.liveEditMode })),
        partialIcon: PartialIcon(StrictObj({ LE: envRaw.liveEditMode })),
        partialButtons: PartialButtons(StrictObj({
          LE: envRaw.liveEditMode,
          partialVote: PartialVote(StrictObj({ LE: envRaw.liveEditMode })),
          partialShare: PartialShare(StrictObj({ LE: envRaw.liveEditMode }))
        }))
      })),
      partialShowcase: PartialShowcase(StrictObj({
        LE: envRaw.liveEditMode,
        partialIcon: PartialIcon(StrictObj({ LE: envRaw.liveEditMode })),
        partialContent: PartialContent(StrictObj({ LE: envRaw.liveEditMode }))
      })),
      partialSwitchers: PartialSwitchers(StrictObj({
        LE: envRaw.liveEditMode,
        LEFT_ICON,
        RIGHT_ICON
      })),
      partialPlaceholder: PartialPlaceholder(StrictObj({ LE: envRaw.liveEditMode }))
    })),
    controller: GalleryModalComponent,
    bindings: {
      ggsPullElm: '<',
      ggsStylerNew: '<',
      ggsProperties: '<',
      ggsPostVoteEmitter: '<',
      ggsVoteChangeHandler: '<',
      ggsModalReadyHandler: '<',
      ggsModalResizeHandler: '<',
      ggsModalAttachHandler: '<',
      ggsModalDetachHandler: '<',
      ggsModalPrevRequestHandler: '<',
      ggsModalNextRequestHandler: '<',
      ggsVoteCountHideHandler: '<',
      ggsVotingDisallowHandler: '<'
    }
  })

const ALLOW_DELAY = 0.8e3

function GalleryModalComponent(
  qh,
  $log,
  $scope,
  $element,
  debugObj,
  addIdPrefix,
  modalLayout,
  DefineHandler,
  DefineMethod,
  LazyResource,
  modalDrawBounds,
  calcShowcaseSize,
  GalleryModalStyleHandlers) {

  // main
  const instanceId = uniqueId()
  const $ctrl = $scope.$ctrl = attachGetters(attachSetters(StrictObj(this)))
  const sign = `galleryModal[uid=${instanceId}]`
  debugObj(sign, $ctrl)

  $ctrl.wrapM = DefineMethod(sign)
  $ctrl.wrapH = DefineHandler(sign)

  $ctrl.resetState = () => {
    $ctrl.ready = false
    // layout
    $ctrl.layoutName = null
    $ctrl.layoutText = false
    $ctrl.layoutTiny = false
    // prev/next
    $ctrl.hasPrev = false
    $ctrl.hasNext = false
    // icon
    $ctrl.showsShowcaseIcon = true
    // sizes
    $ctrl.showcaseBounds = null
    $ctrl.showcaseSize = null
    $ctrl.headSize = null
    $ctrl.paneSize = null
    $ctrl.paneDescSize = null
    $ctrl.footerSize = null
    // local
    $ctrl.isCurrModal = false
    $ctrl.isCurrCount = 0
    // slides
    $ctrl.slideIndex = 0
  }

  $ctrl.resetState()

  // ggsPostInfo
  const relaxedGetPostInfo = (post = null) =>
    isPlainObject(post)
      ? getPostInfo(post)
      : blankPostInfo

  DependentValue({
    obj: $ctrl,
    key: 'ggsPostInfo',
    dependent: relaxedGetPostInfo,
    dependency: $ctrl.getters.ggsPullElm.data.terminate
  })

  // styles
  const stylesEmitter = SumEmitter(
    $ctrl.ggsProperties.key2emitter.allow_voting,
    $ctrl.ggsProperties.key2emitter.allow_sharing,
    $ctrl.ggsProperties.key2emitter.show_vote_count,
    $ctrl.ggsProperties.key2emitter.show_source_in_modal,
    $ctrl.ggsProperties.key2emitter.show_post_desc_in_modal,
    $ctrl.ggsProperties.key2emitter.show_user_info_in_modal,
    $ctrl.ggsProperties.key2emitter.modal_force_vertical_layout,
    $ctrl.ggsProperties.key2emitter.modal_show_branding_footer,
    $ctrl.ggsProperties.key2emitter.modal_attach_branding_footer,
    $ctrl.ggsProperties.key2emitter.modals_show_close_button,
    $ctrl.ggsProperties.key2emitter.modal_show_switch_buttons)

  const modalElmId = addIdPrefix(`gallery-modal-${instanceId}`)
  const styleHandlers = GalleryModalStyleHandlers(modalElmId)
  $element.attr('id', modalElmId)

  // emitters
  $ctrl.sizeEmitter = Emitter(`${sign}::size`)
  $ctrl.readyEmitter = Emitter(`${sign}::ready`)
  $ctrl.layoutEmitter = Emitter(`${sign}::layout`)

  // debug bounds
  if (envRaw.showsModalBounds) {
    $scope.$watch(
      flow(
        $ctrl.getters.showcaseBounds,
        wrap($element, modalDrawBounds)), noop)
  }

  // layout
  $ctrl.isTextLayout = attachEmitters(
    $ctrl.layoutEmitter,
    $ctrl.getters.layoutText)

  $ctrl.isTinyLayout = attachEmitters(
    $ctrl.layoutEmitter,
    $ctrl.getters.layoutTiny)

  $ctrl.isVerLayout = attachEmitters(
    $ctrl.layoutEmitter,
    makeEQ($ctrl.getters.layoutName, constant(modalLayout.VER_LAYOUT)))

  $ctrl.isHorLayout = attachEmitters(
    $ctrl.layoutEmitter,
    makeEQ($ctrl.getters.layoutName, constant(modalLayout.HOR_LAYOUT)))

  // has
  $ctrl.hasIcon = attachEmitters(
    $ctrl.ggsPullElm.emitter,
    $ctrl.ggsProperties.key2emitter.show_source_in_modal,
    makeAND(
      $ctrl.getters.ggsPullElm.used,
      $ctrl.getters.negate.ggsPostInfo.isUploadHost,
      $ctrl.getters.negate.ggsPostInfo.isUnknown,
      $ctrl.ggsProperties.key2get.show_source_in_modal))

  $ctrl.hasVoteButton = attachEmitters(
    $ctrl.ggsProperties.key2emitter.allow_voting,
    $ctrl.ggsProperties.key2emitter.show_vote_count,
    makeOR(
      $ctrl.ggsProperties.key2get.allow_voting,
      $ctrl.ggsProperties.key2get.show_vote_count))

  $ctrl.hasShareButton = attachEmitters(
    $ctrl.ggsProperties.key2emitter.allow_sharing,
    $ctrl.ggsProperties.key2get.allow_sharing)

  // hasHead
  $ctrl.hasHeadUser = attachEmitters(
    $ctrl.isVerLayout,
    $ctrl.ggsProperties.key2emitter.show_user_info_in_modal,
    makeAND(
      $ctrl.isVerLayout,
      $ctrl.ggsProperties.key2get.show_user_info_in_modal))

  $ctrl.hasHeadIcon = attachEmitters(
    $ctrl.isVerLayout,
    $ctrl.hasIcon,
    makeAND(
      $ctrl.isVerLayout,
      $ctrl.hasIcon))

  $ctrl.hasHead = attachEmitters(
    $ctrl.hasHeadUser,
    $ctrl.hasHeadIcon,
    makeOR(
      $ctrl.hasHeadUser,
      $ctrl.hasHeadIcon))

  // hasShowcase
  $ctrl.hasShowcaseIcon = attachEmitters(
    $ctrl.isHorLayout,
    $ctrl.hasIcon,
    makeAND(
      $ctrl.isHorLayout,
      $ctrl.hasIcon))

  // hasPane
  $ctrl.hasPaneButtons = attachEmitters(
    $ctrl.hasVoteButton,
    $ctrl.hasShareButton,
    makeOR(
      $ctrl.hasVoteButton,
      $ctrl.hasShareButton))

  $ctrl.hasPaneUser = attachEmitters(
    $ctrl.isHorLayout,
    $ctrl.ggsProperties.key2emitter.show_user_info_in_modal,
    makeAND(
      $ctrl.isHorLayout,
      $ctrl.ggsProperties.key2get.show_user_info_in_modal))

  $ctrl.hasPaneDesc = attachEmitters(
    $ctrl.isHorLayout,
    $ctrl.ggsPullElm.emitter,
    $ctrl.ggsProperties.key2emitter.show_post_desc_in_modal,
    makeAND(
      $ctrl.ggsProperties.key2get.show_post_desc_in_modal,
      $ctrl.getters.ggsPullElm.used,
      makeOR(
        $ctrl.isHorLayout,
        flow($ctrl.getters.ggsPullElm.data.terminate, property('text_full'), isText))))

  $ctrl.hasPane = attachEmitters(
    $ctrl.hasPaneButtons,
    $ctrl.hasPaneUser,
    $ctrl.hasPaneDesc,
    makeOR(
      $ctrl.hasPaneButtons,
      $ctrl.hasPaneUser,
      $ctrl.hasPaneDesc))

  // hasFooter
  $ctrl.hasFooter = attachEmitters(
    $ctrl.ggsProperties.key2emitter.modal_show_branding_footer,
    $ctrl.ggsProperties.key2get.modal_show_branding_footer)

  // <gallery-vote-frame/>
  const extVoteFrame = LazyResource('hidePopups')
  $ctrl.wrapH('voteFrameAttachCallback', (extVoteFrameHidePopups) => {
    extVoteFrame.hidePopups.replace(extVoteFrameHidePopups)
  })

  // <ggs-sharing/>
  const extSharing = LazyResource('hideTooltips')
  $ctrl.wrapH('sharingAttachCallback', (extSharingHideTooltips) => {
    extSharing.hideTooltips.replace(extSharingHideTooltips)
  })

  // <ggs-gallery-embed/>
  const extEmbed = LazyResource(
    'allow',
    'debounce',
    'disallow')

  $ctrl.wrapH('embedAttachCallback', (
    extEmbedAllow,
    extEmbedDebounce,
    extEmbedDisallow) => {

    extEmbed.allow.replace(extEmbedAllow)
    extEmbed.debounce.replace(extEmbedDebounce)
    extEmbed.disallow.replace(extEmbedDisallow)
  })

  $ctrl.wrapH('embedLoadHandler',
    qh.makeIf(
      $ctrl.getters.ggsPullElm.used,
      $ctrl.setters.bindFalse.showsShowcaseIcon))

  $ctrl.wrapH('embedUnloadHandler',
    qh.makeIf(
      $ctrl.getters.ggsPullElm.used,
      $ctrl.setters.bindTrue.showsShowcaseIcon))

  //    <ggs-gallery-embed/>
  // or <ggs-gallery-image/>
  // or <ggs-gallery-broken/>
  const extContent = LazyResource('applySize').sync('calcWidth', 'calcHeight')

  $ctrl.wrapH('contentAttachCallback', (
    extContentCalcWidth,
    extContentCalcHeight,
    extContentApplySize) => {

    extContent.calcWidth.replace(extContentCalcWidth)
    extContent.calcHeight.replace(extContentCalcHeight)
    extContent.applySize.replace(extContentApplySize)
  })

  $ctrl.wrapH('contentReadyHandler',
    qh.makeIf($ctrl.getters.ggsPullElm.used, () => {
      $ctrl.ready = true
      return qh.over(
        qh.makeJoin(
          $ctrl.recalcLayoutName,
          $ctrl.recalcShowcaseSize),
        $ctrl.emitReadyChanged)
    }))

  $ctrl.wrapH('contentChangeHandler',
    qh.makeIf(makeAND($ctrl.getters.ready, $ctrl.getters.ggsPullElm.used), () => {
      $ctrl.showcaseBounds = null
      $ctrl.showcaseSize = null
      return $ctrl.recalcShowcaseSize()
    }))

  // <ggs-gallery-slider/>
  const extSlider = LazyResource(
    'hookPrev',
    'hookNext',
    'nthSlide',
    'lastSlide',
    'firstSlide')

  $ctrl.wrapH('sliderAttachCallback', (
    extSliderHookPrev,
    extSliderHookNext,
    extSliderNthSlide,
    extSliderLastSlide,
    extSliderFirstSlide) => {

    extSlider.hookPrev.replace(extSliderHookPrev)
    extSlider.hookNext.replace(extSliderHookNext)
    extSlider.nthSlide.replace(extSliderNthSlide)
    extSlider.lastSlide.replace(extSliderLastSlide)
    extSlider.firstSlide.replace(extSliderFirstSlide)
  })

  $ctrl.wrapH('sliderChangeHandler', (slideIndex) => {
    $ctrl.slideIndex = slideIndex
  })

  // extModalApplyState
  $ctrl.wrapM('manageCurr', (postHash) => {
    const actIsCurr = $ctrl.ggsPullElm.data === postHash.currPost
    if (actIsCurr !== $ctrl.isCurrModal) {
      $ctrl.isCurrModal = actIsCurr
      $ctrl.isCurrCount++
    }
  })

  $ctrl.wrapM('manageEmbed', (postHash) => {
    if (!postHash.currChanged) {
      return
    }

    if (!$ctrl.isCurrModal) {
      return extEmbed.disallow()
    }

    const dbncP = extEmbed.debounce()
    const allowP = qh.delay(ALLOW_DELAY).then(
      qh.makeIf(
        makeAND(
          makeEQ($ctrl.getters.isCurrCount, constant($ctrl.isCurrCount)),
          makeEQ($ctrl.getters.ggsPullElm.count, constant($ctrl.ggsPullElm.count))),
        extEmbed.allow))

    return qh.join(dbncP, allowP)
  })

  $ctrl.wrapM('managePrevNext', (postHash) => {
    $ctrl.hasPrev = $ctrl.isCurrModal && !!postHash.prevPost
    $ctrl.hasNext = $ctrl.isCurrModal && !!postHash.nextPost
  })

  $ctrl.wrapM('manageSharing',
    qh.makeIf(
      makeAND(
        $ctrl.hasShareButton,
        $ctrl.getters.negate.isCurrModal),
      extSharing.hideTooltips))

  $ctrl.wrapM('manageVoteFrame',
    qh.makeIf(
      makeAND(
        $ctrl.hasVoteButton,
        $ctrl.getters.negate.isCurrModal),
      extVoteFrame.hidePopups))

  $ctrl.wrapM('extModalApplyState',
    qh.makeIf(
      $ctrl.getters.ggsPullElm.used,
      qh.makeJoin(
        $ctrl.manageCurr,
        $ctrl.manageEmbed,
        $ctrl.manageSharing,
        $ctrl.managePrevNext,
        $ctrl.manageVoteFrame)))

  // other external methods
  $ctrl.wrapM('extModalHookPrev',
    qh.makeIf(
      $ctrl.getters.isCurrModal,
      extSlider.hookPrev))

  $ctrl.wrapM('extModalHookNext',
    qh.makeIf(
      $ctrl.getters.isCurrModal,
      extSlider.hookNext))

  $ctrl.wrapM('extModalNthSlide',
    qh.makeIf(
      $ctrl.getters.isCurrModal,
      extSlider.nthSlide))

  $ctrl.wrapM('extModalLastSlide',
    qh.makeIf(
      $ctrl.getters.isCurrModal,
      extSlider.lastSlide))

  $ctrl.wrapM('extModalFirstSlide',
    qh.makeIf(
      $ctrl.getters.isCurrModal,
      extSlider.firstSlide))

  // recalcShowcaseSize
  $ctrl.wrapM('recalcShowcaseSize', () => {
    const attachesFooter = true
      && $ctrl.ggsProperties.modal_show_branding_footer
      && $ctrl.ggsProperties.modal_attach_branding_footer

    const showsCloseButton = $ctrl.ggsProperties.modals_show_close_button
    const showsSwitchButtons = $ctrl.ggsProperties.modal_show_switch_buttons

    const horPaneWidth = modalLayout.calcHorPaneWidth($ctrl.layoutTiny)
    $ctrl.showcaseBounds = modalLayout.calcShowcaseBounds(
      horPaneWidth,
      $ctrl.layoutName,
      $ctrl.layoutText,
      $ctrl.layoutTiny,
      attachesFooter,
      showsCloseButton,
      showsSwitchButtons,
      $ctrl.hasHead(),
      $ctrl.hasPaneButtons(),
      $ctrl.hasPaneDesc(),
      $ctrl.hasPaneUser())

    const oldShowcaseSize = $ctrl.showcaseSize
    $ctrl.showcaseSize = calcShowcaseSize(
      $ctrl.layoutText,
      $ctrl.showcaseBounds,
      extContent.calcWidth,
      extContent.calcHeight)

    const oldHeadSize = $ctrl.headSize
    $ctrl.headSize = StrictObj({
      width: $ctrl.showcaseSize.width
    })

    const oldPaneSize = $ctrl.paneSize
    const horPaneSize = StrictObj({
      width: horPaneWidth,
      height: $ctrl.showcaseSize.height
    })
    const verPaneSize = StrictObj({
      width: $ctrl.showcaseSize.width,
      height: null
    })
    $ctrl.paneSize =
      $ctrl.layoutName === modalLayout.HOR_LAYOUT
        ? horPaneSize
        : verPaneSize

    const oldPaneDescSize = $ctrl.paneDescSize
    const horPaneDescSize = StrictObj({
      maxHeight: null
    })
    const verPaneDescSize = StrictObj({
      maxHeight:
        modalLayout.calcVerPaneDescMaxHeight(
          $ctrl.showcaseSize.height,
          $ctrl.layoutText,
          $ctrl.layoutTiny,
          attachesFooter,
          showsCloseButton,
          $ctrl.hasHead(),
          $ctrl.hasPaneButtons(),
          $ctrl.hasPaneDesc())
    })
    $ctrl.paneDescSize =
      $ctrl.layoutName === modalLayout.HOR_LAYOUT
        ? horPaneDescSize
        : verPaneDescSize

    const oldFooterSize = $ctrl.footerSize
    const horFooterSize = StrictObj({
      width: $ctrl.showcaseSize.width + $ctrl.paneSize.width
    })
    const verFooterSize = StrictObj({
      width: $ctrl.showcaseSize.width
    })
    $ctrl.footerSize =
      $ctrl.layoutName === modalLayout.HOR_LAYOUT
        ? horFooterSize
        : verFooterSize

    const sizeChanged = false
      || !isEqual($ctrl.showcaseSize, oldShowcaseSize)
      || !isEqual($ctrl.headSize, oldHeadSize)
      || !isEqual($ctrl.paneSize, oldPaneSize)
      || !isEqual($ctrl.paneDescSize, oldPaneDescSize)
      || !isEqual($ctrl.footerSize, oldFooterSize)

    if (sizeChanged) {
      $ctrl.sizeEmitter.emit($ctrl.showcaseSize)
    }
  })

  // recalcLayoutName
  $ctrl.wrapM('recalcLayoutName', () => {
    const oldText = $ctrl.layoutText
    $ctrl.layoutText = $ctrl.ggsPullElm.data.preview.info.isSomeText

    const forceVer = false
      || $ctrl.layoutText
      || $ctrl.ggsProperties.modal_force_vertical_layout
      || !$ctrl.ggsProperties.show_post_desc_in_modal
      || !isText($ctrl.ggsPullElm.data.text_full)

    const oldName = $ctrl.layoutName
    $ctrl.layoutName = forceVer
      ? modalLayout.VER_LAYOUT
      : modalLayout.calcName()

    const oldTiny = $ctrl.layoutTiny
    $ctrl.layoutTiny = modalLayout.calcTiny()

    const layoutChanged = false
      || oldText !== $ctrl.layoutText
      || oldName !== $ctrl.layoutName
      || oldTiny !== $ctrl.layoutTiny

    if (layoutChanged) {
      $ctrl.layoutEmitter.emit($ctrl.layoutName, $ctrl.layoutTiny)
    }
  })

  // resizeHandler
  $ctrl.wrapH('resizeHandler',
    qh.makeIf(
      makeAND(
        $ctrl.getters.ready,
        $ctrl.getters.ggsPullElm.used),
      qh.makeJoin(
        $ctrl.recalcLayoutName,
        $ctrl.recalcShowcaseSize)))

  // postChangeHandler
  $ctrl.wrapM('emitReadyChanged', () => {
    $ctrl.readyEmitter.emit($ctrl.ready)
  })

  $ctrl.wrapH('postChangeHandler',
    qh.makeOver(
      $ctrl.resetState,
      $ctrl.emitReadyChanged))

  // applySize
  $ctrl.wrapM('applySize', () => {
    return extContent.applySize($ctrl.showcaseSize)
  })

  // init
  stylesEmitter.on($ctrl.resizeHandler)
  modalLayout.resizeEmitter.on($ctrl.resizeHandler)
  $ctrl.sizeEmitter.on($ctrl.applySize)
  $ctrl.sizeEmitter.on($ctrl.ggsModalResizeHandler)
  $ctrl.readyEmitter.on($ctrl.ggsModalReadyHandler)
  $ctrl.ggsPullElm.emitter.pre($ctrl.postChangeHandler)

  $ctrl.wrapM('attachToStyler',
    qh.makeCatch(
      wrap(styleHandlers, $ctrl.ggsStylerNew.reg),
      wrap(`${sign}#attachToStyler: error halted:`, $log.error)))

  $ctrl.wrapM('attachToParent', () => {
    return $ctrl.ggsModalAttachHandler(
      $ctrl.extModalHookPrev,
      $ctrl.extModalHookNext,
      $ctrl.extModalNthSlide,
      $ctrl.extModalLastSlide,
      $ctrl.extModalFirstSlide,
      $ctrl.extModalApplyState)
  })

  $ctrl.wrapH('$onInit',
    qh.makeJoin(
      $ctrl.attachToStyler,
      $ctrl.attachToParent))

  // destroy
  $ctrl.wrapM('detachFromStyler',
    wrap(styleHandlers, $ctrl.ggsStylerNew.release))

  $ctrl.wrapM('detachFromParent', () => {
    return $ctrl.ggsModalDetachHandler(
      $ctrl.extModalHookPrev,
      $ctrl.extModalHookNext,
      $ctrl.extModalNthSlide,
      $ctrl.extModalLastSlide,
      $ctrl.extModalFirstSlide,
      $ctrl.extModalApplyState)
  })

  $ctrl.wrapM('detachFromEmitters',
    qh.makeJoin(
      wrap($ctrl.resizeHandler, stylesEmitter.off),
      wrap($ctrl.resizeHandler, modalLayout.resizeEmitter.off),
      wrap($ctrl.applySize, $ctrl.sizeEmitter.off),
      wrap($ctrl.ggsModalResizeHandler, $ctrl.sizeEmitter.off),
      wrap($ctrl.ggsModalReadyHandler, $ctrl.readyEmitter.off),
      wrap($ctrl.postChangeHandler, $ctrl.ggsPullElm.emitter.off)))

  $ctrl.wrapH('$onDestroy',
    qh.makeJoin(
      wrap($ctrl, debugObj.remove),
      $ctrl.detachFromStyler,
      $ctrl.detachFromParent,
      $ctrl.detachFromEmitters))
}
